import { BLOCK_URL_BASE_LIST } from '@/presession/authentication/types';
import {
  ILogger,
  WarpDriveLoginType,
  WSBrokerServiceConstants,
} from '@amzn/work-spaces-web-client-core-lib';

/**
 * Utility class for detecting login types from URLs
 */
export class LoginTypeDetector {
  private static matchesPatternSecurely(url: string, pattern: string): boolean {
    let host: string;
    try {
      host = new URL(url).host;
    } catch {
      return false;
    }

    if (pattern.startsWith('.')) {
      return host.endsWith(pattern) || host === pattern.substring(1);
    }
    if (pattern.includes('/')) {
      return url.includes(pattern);
    }
    return host === pattern || host.endsWith('.' + pattern);
  }

  static getUrlHost(url: string | undefined): string {
    if (!url) {
      return 'EMPTY_URL';
    }
    try {
      return new URL(url).host;
    } catch {
      return 'INVALID_URL';
    }
  }

  static isConnectivityUrl(url: string | undefined): boolean {
    if (!url) {
      return false;
    }
    return BLOCK_URL_BASE_LIST.some((blockUrl) => url.includes(blockUrl));
  }

  /**
   * Main public method that detects the login type based on the URL pattern.
   * This method handles both direct login URLs and block URLs with redirect parameters.
   *
   * For direct login URLs, it matches against known patterns for WD_LOGIN and UNIFIED_LOGIN.
   * For block URLs (connectivity.amazonworkspaces.com/block), it delegates to detectBlockType
   * to analyze the redirectUrl parameter.
   *
   * @param url - The URL to analyze
   * @param logger - Optional logger for logging detection results
   * @returns The detected login type (WD_LOGIN, UNIFIED_LOGIN, HARD_BLOCK, or UNKNOWN)
   */
  static detectLoginTypeFromUrl(
    url: string | undefined,
    logger?: ILogger
  ): WarpDriveLoginType {
    if (!url) {
      logger?.warn('Empty URL provided for login type detection');
      return WarpDriveLoginType.UNKNOWN;
    }

    if (this.isConnectivityUrl(url)) {
      logger?.info('Detected connectivity URL, checking redirect destination');
      return this.detectBlockType(url, logger);
    }

    if (
      WSBrokerServiceConstants.WD_LOGIN_PATTERNS.some((pattern) =>
        this.matchesPatternSecurely(url, pattern)
      )
    ) {
      logger?.info(`Detected WD_LOGIN from URL: ${url}`);
      return WarpDriveLoginType.WD_LOGIN;
    }

    if (
      WSBrokerServiceConstants.UNIFIED_LOGIN_PATTERNS.some((pattern) =>
        this.matchesPatternSecurely(url, pattern)
      )
    ) {
      logger?.info(`Detected UNIFIED_LOGIN from URL: ${url}`);
      return WarpDriveLoginType.UNIFIED_LOGIN;
    }
    logger?.warn('No known login pattern matched, returning UNKNOWN');
    return WarpDriveLoginType.UNKNOWN;
  }

  /**
   * Private helper method that specifically handles block URLs.
   * This method is called by detectLoginTypeFromUrl when a block URL is detected.
   *
   * It extracts the redirectUrl parameter from block URLs and analyzes the redirect
   * destination to determine the appropriate login type. This is different from
   * detectLoginTypeFromUrl because it specifically handles the URL parsing and
   * decoding logic for redirect scenarios.
   *
   * @param url - The block URL containing a redirectUrl parameter
   * @param logger - Optional logger for logging detection results
   * @returns The detected login type based on the redirect URL (WD_LOGIN, UNIFIED_LOGIN, HARD_BLOCK, or UNKNOWN)
   */
  private static detectBlockType(
    url: string,
    logger?: ILogger
  ): WarpDriveLoginType {
    try {
      const redirectUrl = new URL(url).searchParams.get('redirectUrl');

      if (!redirectUrl) {
        logger?.warn(
          'No redirectUrl found in connectivity URL, returning HARD_BLOCK'
        );
        return WarpDriveLoginType.HARD_BLOCK;
      }

      const decodedUrl = decodeURIComponent(redirectUrl);

      if (
        WSBrokerServiceConstants.WD_LOGIN_PATTERNS.some((pattern) =>
          this.matchesPatternSecurely(decodedUrl, pattern)
        )
      ) {
        logger?.info(`Detected WD_LOGIN from URL: ${url}`);
        return WarpDriveLoginType.WD_LOGIN;
      }
      if (
        WSBrokerServiceConstants.UNIFIED_LOGIN_PATTERNS.some((pattern) =>
          this.matchesPatternSecurely(decodedUrl, pattern)
        )
      ) {
        logger?.info(`Detected UNIFIED_LOGIN from URL: ${url}`);
        return WarpDriveLoginType.UNIFIED_LOGIN;
      }

      logger?.warn('Unknown redirect pattern, defaulting to UNIFIED_LOGIN');
      return WarpDriveLoginType.UNIFIED_LOGIN;
    } catch {
      return WarpDriveLoginType.UNKNOWN;
    }
  }
}
